// ============================================================================
// ui/dom.js — tiny DOM helpers (browser-only; kept out of the tested core)
// ============================================================================
// A minimal hyperscript `h()` + a couple of utilities so the step modules stay
// declarative without a framework. Everything the Weight Designer renders goes
// through here so class/attr/event wiring is consistent.
// ============================================================================

/**
 * Hyperscript element builder.
 *   h('button', { class: 'wd-btn', onClick: fn, 'aria-pressed': 'true' }, 'Go')
 * - `class`      → className
 * - `html`       → innerHTML (trusted callers only)
 * - `dataset`    → Object.assign(el.dataset, ...)
 * - `on<Event>`  → addEventListener (function value)
 * - null/undefined prop values are skipped; falsy children are skipped.
 */
export function h(tag, props, ...children) {
  const node = document.createElement(tag);
  if (props) {
    for (const key in props) {
      const val = props[key];
      if (val == null || val === false) continue;
      if (key === 'class') node.className = val;
      else if (key === 'html') node.innerHTML = val;
      else if (key === 'dataset') Object.assign(node.dataset, val);
      else if (key.startsWith('on') && typeof val === 'function') {
        node.addEventListener(key.slice(2).toLowerCase(), val);
      } else if (key === 'style' && typeof val === 'object') {
        Object.assign(node.style, val);
      } else {
        node.setAttribute(key, val === true ? '' : val);
      }
    }
  }
  appendChildren(node, children);
  return node;
}

function appendChildren(node, children) {
  for (const child of children) {
    if (child == null || child === false) continue;
    if (Array.isArray(child)) appendChildren(node, child);
    else node.append(child.nodeType ? child : document.createTextNode(String(child)));
  }
}

/** Replace all children of `node` with `next` (element, array, or null). */
export function replaceChildren(node, next) {
  node.replaceChildren();
  if (next == null) return;
  appendChildren(node, Array.isArray(next) ? next : [next]);
}

/** Query within a root (defaults to document). */
export function qs(sel, root = document) {
  return root.querySelector(sel);
}

/** matchMedia guarded for non-browser/older environments. */
export function media(query) {
  return typeof matchMedia === 'function' ? matchMedia(query) : { matches: false, addEventListener() {} };
}
